/**
 * createGridAdapter.ts — wraps an ag-Grid infinite-model GridApi so the resource
 * viewer's prev/next can page through the result set by index. This is the React
 * analogue of Angular's `_browseApi` + `gridNavigateRows` parking logic
 * (ResourceController.ts:551, resolved by DataGridBlockLoadedEvent :492).
 *
 * Prev/next targets are almost always adjacent to the current row and already in
 * the loaded block (cacheBlockSize=100), so ensureIndexLoaded returns synchronously
 * in the common case; the poll only matters when navigation crosses a block edge.
 */

import type { GridApi } from 'ag-grid-community';
import type { GridAdapter, NormalizedRow } from '../types/resource';

const POLL_INTERVAL_MS = 50;
const POLL_TIMEOUT_MS = 5000;

export function createGridAdapter(api: GridApi, getTotalRows: () => number): GridAdapter {
  const rowAt = (index: number): NormalizedRow | null => {
    const node = api.getDisplayedRowAtIndex(index);
    return (node?.data as NormalizedRow | undefined) ?? null;
  };

  return {
    getRowCount: () => getTotalRows(),

    getRowAt: rowAt,

    // Resolve the row at an arbitrary index, loading its block if needed. Mirrors
    // the park-then-retry: request the block via ensureIndexVisible, then wait for
    // the row to materialise (ag-Grid fills node.data when the block arrives).
    ensureIndexLoaded: (index: number): Promise<NormalizedRow | null> => {
      const existing = rowAt(index);
      if (existing) return Promise.resolve(existing);

      // Triggers the datasource fetch for the block containing `index`.
      api.ensureIndexVisible(index);

      return new Promise((resolve) => {
        const started = Date.now();
        const timer = setInterval(() => {
          const row = rowAt(index);
          if (row) {
            clearInterval(timer);
            resolve(row);
          } else if (Date.now() - started > POLL_TIMEOUT_MS) {
            clearInterval(timer);
            resolve(null);
          }
        }, POLL_INTERVAL_MS);
      });
    },

    // Scroll the grid to keep the viewed row visible. Cell-focus highlight (which
    // needs columnApi, not exposed here in ag-Grid v22) is deferred to M3.
    focus: (index: number) => {
      api.ensureIndexVisible(index);
    },
  };
}
